import React, { useState, useEffect } from 'react';
import { getTenantId } from '../utils/tenant';
import { getAccessToken } from '../utils/storage';

interface Task {
  task_id: string;
  status: string;
  task_type: string;
  created_at: string;
  progress: number;
  message: string;
}

interface TaskDetail {
  task_id: string;
  task_type: string;
  tenant_id: string;
  status: string;
  progress: number;
  message: string;
  created_at: string;
  started_at: string;
  completed_at: string;
  steps: {
    name: string;
    status: string;
    timestamp: string;
    completed_at: string;
  }[];
  current_step: string;
  error: string | null;
  metadata: {
    db_host: string;
    db_name: string;
    estimated_duration: string;
  };
}

interface TasksProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

const Tasks: React.FC<TasksProps> = ({ isOpen, onClose, onBack }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('');

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
    const tenantId = getTenantId();
      
      const params = new URLSearchParams();
      // if (statusFilter) params.append('status_filter', statusFilter);
      // if (taskTypeFilter) params.append('task_type', taskTypeFilter);
      params.append('limit', '50');

      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/tasks/tenant/${tenantId}?${params}`, {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskDetail = async (taskId: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/tasks/${taskId}`, {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to fetch task details');
      const data = await response.json();
      setSelectedTask(data);
      setShowTaskDetail(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch task details');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
    }
  }, [isOpen, statusFilter, taskTypeFilter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 w-[650px] max-w-full shadow-2xl border-2 border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
                arrow_back
              </span>
            </button>
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>
                  assignment
                </span>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Tasks</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
              close
            </span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 px-4 py-2.5 border-2 border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={taskTypeFilter}
            onChange={(e) => setTaskTypeFilter(e.target.value)}
            className="flex-1 px-4 py-2.5 border-2 border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
          >
            <option value="">All Types</option>
            <option value="embedding">Embedding</option>
            <option value="schema">Schema</option>
            <option value="import">Import</option>
          </select>
        </div>

        {/* Tasks List */}
        <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mb-4"></div>
              <div className="text-slate-600 dark:text-slate-400 font-medium">Loading tasks...</div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-red-600 dark:text-red-400" style={{ fontSize: '32px' }}>error</span>
              </div>
              <div className="text-red-600 dark:text-red-400 font-medium">{error}</div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '32px' }}>inbox</span>
              </div>
              <div className="text-slate-500 dark:text-slate-400 font-medium">No tasks found</div>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <button
                  key={task.task_id}
                  onClick={() => fetchTaskDetail(task.task_id)}
                  disabled={detailLoading}
                  className="w-full border-2 border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-900 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-slate-800 dark:hover:to-slate-850 hover:border-blue-300 dark:hover:border-blue-600 transition-all text-left disabled:opacity-50 shadow-sm hover:shadow-md group"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{task.task_type}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${
                          task.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                          task.status === 'running' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                          task.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400 mb-2 line-clamp-2">
                        {task.message}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-500">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>schedule</span>
                        <span>{new Date(task.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2">
                      <div className="text-xs text-slate-400 dark:text-slate-600 font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                        {task.task_id.slice(0, 8)}
                      </div>
                      <span className="material-symbols-outlined text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" style={{ fontSize: '20px' }}>
                        chevron_right
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Task Detail Modal */}
      {showTaskDetail && selectedTask && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-60 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 w-[700px] max-w-full max-h-[80vh] overflow-y-auto shadow-2xl border-2 border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '20px' }}>
                    description
                  </span>
                </div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Task Details</h3>
              </div>
              <button
                onClick={() => setShowTaskDetail(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all hover:shadow-md"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-850 rounded-xl p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Task ID</label>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-1">{selectedTask.task_id}</div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Type</label>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-1">{selectedTask.task_type}</div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Status</label>
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-semibold mt-1 shadow-sm ${
                      selectedTask.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                      selectedTask.status === 'running' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                      selectedTask.status === 'failed' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                      'bg-gradient-to-r from-yellow-500 to-orange-600 text-white'
                    }`}>{selectedTask.status}</div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Progress</label>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-1">{selectedTask.progress}%</div>
                  </div>
                </div>
              </div>
              
              {/* Message */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-slate-900 dark:to-slate-850 rounded-xl p-5 border-2 border-blue-200 dark:border-slate-700 shadow-sm">
                <label className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide flex items-center gap-2">
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>message</span>
                  Message
                </label>
                <div className="text-sm text-slate-800 dark:text-slate-200 mt-2 leading-relaxed">{selectedTask.message}</div>
              </div>
              
              {/* Timestamps */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-850 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>schedule</span>
                    Created
                  </label>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-2">{new Date(selectedTask.created_at).toLocaleString()}</div>
                </div>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-850 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>play_arrow</span>
                    Started
                  </label>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-2">{selectedTask.started_at ? new Date(selectedTask.started_at).toLocaleString() : 'N/A'}</div>
                </div>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-850 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>check_circle</span>
                    Completed
                  </label>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-2">{selectedTask.completed_at ? new Date(selectedTask.completed_at).toLocaleString() : 'N/A'}</div>
                </div>
              </div>
              
              {/* Steps */}
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>list</span>
                  Steps
                </label>
                <div className="space-y-3">
                  {selectedTask.steps.map((step, index) => (
                    <div key={index} className="border-2 border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-850 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex-1">{step.name}</span>
                        <span className={`px-3 py-1 rounded-lg text-xs font-semibold shadow-sm ${
                          step.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                          step.status === 'running' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                          step.status === 'failed' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                          'bg-gradient-to-r from-yellow-500 to-orange-600 text-white'
                        }`}>
                          {step.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>schedule</span>
                        Started: {new Date(step.timestamp).toLocaleString()}
                        {step.completed_at && ` | Completed: ${new Date(step.completed_at).toLocaleString()}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Metadata */}
              {selectedTask.metadata && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>database</span>
                    Metadata
                  </label>
                  <div className="border-2 border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-850 shadow-sm">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400" style={{ fontSize: '18px' }}>storage</span>
                        <div><span className="font-semibold text-slate-700 dark:text-slate-300">DB Host:</span> <span className="text-slate-800 dark:text-slate-200">{selectedTask.metadata.db_host}</span></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-purple-600 dark:text-purple-400" style={{ fontSize: '18px' }}>folder</span>
                        <div><span className="font-semibold text-slate-700 dark:text-slate-300">DB Name:</span> <span className="text-slate-800 dark:text-slate-200">{selectedTask.metadata.db_name}</span></div>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="material-symbols-outlined text-green-600 dark:text-green-400" style={{ fontSize: '18px' }}>timer</span>
                        <div><span className="font-semibold text-slate-700 dark:text-slate-300">Duration:</span> <span className="text-slate-800 dark:text-slate-200">{selectedTask.metadata.estimated_duration}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Error */}
              {selectedTask.error && (
                <div>
                  <label className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>error</span>
                    Error
                  </label>
                  <div className="border-2 border-red-300 dark:border-red-700 rounded-xl p-5 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 text-red-800 dark:text-red-200 text-sm shadow-md">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-red-600 dark:text-red-400 mt-0.5" style={{ fontSize: '20px' }}>warning</span>
                      <div className="flex-1 font-medium leading-relaxed">{selectedTask.error}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;